import React from 'react'
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import { PencilSquareIcon, SparklesIcon, TrashIcon, UsersIcon } from '@heroicons/react/24/outline'
import type { OrgMemberRow } from 'pure-js/types/OrganizationMembership'
import { isParticipantOrgRole } from 'pure-js/libs/organizationMembershipHelpers'

import { Spinner } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { OrgRoleChip } from '../../components/OrgRoleChip'
import { OrgMemberManagePeopleAction } from './OrgMemberManagePeopleAction'
import {
  orgTableBodyCellSx,
  orgTableCardSx,
  orgTableHeadCellSx,
  orgTablePaginationSx,
  orgTableRowSx
} from '../../components/orgTableStyles'

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100]

interface OrgMembersTableProps {
  rows: OrgMemberRow[]
  totalFiltered: number
  isOrgAdmin: boolean
  /** Current user id — admins cannot change their own role (no self-demotion). */
  currentUserId?: string
  onChangeRole: (member: OrgMemberRow) => void
  onRemove: (member: OrgMemberRow) => void
  /** When provided (coach enabled + caller can manage), shows a "re-request goal-setting" action on gamer rows. */
  onRequestGoals?: (member: OrgMemberRow) => void
  /** When provided, leader rows carry the action that opens the people they lead. */
  onManagePeople?: (member: OrgMemberRow) => void
  isLoading: boolean
  isError: boolean
  hasNoMembers: boolean
  hasNoMatches: boolean
  page: number
  rowsPerPage: number
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
}

export const OrgMembersTable: React.FC<OrgMembersTableProps> = ({
  rows,
  totalFiltered,
  isOrgAdmin,
  currentUserId,
  onChangeRole,
  onRemove,
  onRequestGoals,
  onManagePeople,
  isLoading,
  isError,
  hasNoMembers,
  hasNoMatches,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange
}) => {
  const { t } = useTranslation()
  const showPagination = !isLoading && !isError && totalFiltered > rowsPerPage

  return (
    <Box sx={orgTableCardSx}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={orgTableHeadCellSx}>{t('org.members.columns.name')}</TableCell>
              <TableCell sx={orgTableHeadCellSx}>{t('org.members.columns.email')}</TableCell>
              <TableCell sx={orgTableHeadCellSx}>{t('org.members.columns.role')}</TableCell>
              <TableCell sx={orgTableHeadCellSx}>{t('org.members.columns.joined')}</TableCell>
              <TableCell sx={{ ...orgTableHeadCellSx, textAlign: 'right' }}>
                {t('org.members.columns.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...orgTableBodyCellSx, py: 6, textAlign: 'center', border: 'none' }}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}

            {isError && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  sx={{
                    ...orgTableBodyCellSx,
                    py: 6,
                    textAlign: 'center',
                    color: INTERAX_COLORS.redError,
                    border: 'none'
                  }}
                >
                  {t('common.error')}
                </TableCell>
              </TableRow>
            )}

            {hasNoMembers && (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...orgTableBodyCellSx, py: 6, textAlign: 'center', border: 'none' }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <UsersIcon style={{ width: 40, height: 40, color: INTERAX_COLORS.white40 }} />
                    <Typography sx={{ color: INTERAX_COLORS.white70 }}>{t('org.members.emptyNoSearch')}</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {hasNoMatches && (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...orgTableBodyCellSx, py: 6, textAlign: 'center', border: 'none' }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <UsersIcon style={{ width: 40, height: 40, color: INTERAX_COLORS.white40 }} />
                    <Typography sx={{ color: INTERAX_COLORS.white70 }}>{t('org.members.empty')}</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !isError &&
              rows.map((row) => (
                <TableRow key={row.userId} sx={orgTableRowSx} data-testid={`org-members-row-${row.userId}`}>
                  <TableCell sx={orgTableBodyCellSx}>{row.displayName || '—'}</TableCell>
                  <TableCell sx={{ ...orgTableBodyCellSx, wordBreak: 'break-all' }}>{row.email || '—'}</TableCell>
                  <TableCell sx={orgTableBodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <OrgRoleChip role={row.role} size="small" />
                      {isOrgAdmin && row.userId !== currentUserId && (
                        <IconButton
                          size="small"
                          onClick={() => onChangeRole(row)}
                          aria-label={t('org.members.changeRoleTitle')}
                          data-testid="org-members-row-change-role"
                          sx={{
                            color: INTERAX_COLORS.white70,
                            '&:hover': {
                              color: INTERAX_COLORS.sun,
                              backgroundColor: INTERAX_COLORS.white04
                            }
                          }}
                        >
                          <PencilSquareIcon style={{ width: 16, height: 16 }} />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ ...orgTableBodyCellSx, color: INTERAX_COLORS.white70 }}>
                    {row.joinedAt ? dayjs(row.joinedAt).format('DD MMM YYYY') : '—'}
                  </TableCell>
                  <TableCell sx={{ ...orgTableBodyCellSx, textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <OrgMemberManagePeopleAction member={row} onManagePeople={onManagePeople} />
                      {onRequestGoals && isParticipantOrgRole(row.role) && (
                        <IconButton
                          size="small"
                          onClick={() => onRequestGoals(row)}
                          aria-label={t('org.members.requestGoals')}
                          data-testid="org-members-row-request-goals"
                          sx={{
                            color: INTERAX_COLORS.white70,
                            '&:hover': { color: INTERAX_COLORS.sun, backgroundColor: INTERAX_COLORS.white04 }
                          }}
                        >
                          <SparklesIcon style={{ width: 16, height: 16 }} />
                        </IconButton>
                      )}
                      {isOrgAdmin && row.userId !== currentUserId && (
                        <IconButton
                          size="small"
                          onClick={() => onRemove(row)}
                          aria-label={t('org.members.remove')}
                          data-testid="org-members-row-remove"
                          sx={{
                            color: INTERAX_COLORS.redError,
                            '&:hover': { backgroundColor: INTERAX_COLORS.redErrorHover }
                          }}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <TablePagination
          component="div"
          count={totalFiltered}
          page={page}
          onPageChange={(_, p) => onPageChange(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage={t('org.members.rowsPerPage') as string}
          sx={orgTablePaginationSx}
        />
      )}
    </Box>
  )
}
